import React from 'react';

import { Link } from 'react-router-dom';

import { DeploymentUnitOutlined } from '@ant-design/icons';
import { ErpOrderStatus } from '@recurrency/core-api-schema/dist/common/enums';
import { SalesOrderDetailLineDTO } from '@recurrency/core-api-schema/dist/v2/salesOrders/getSalesOrderDetails';
import { ColumnType } from 'antd/lib/table';

import { Button } from 'components/Button';
import { ErpOrderStatusBadge } from 'components/recipes/ErpOrderStatusBadge';
import { Table } from 'components/Table';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { showAsyncModal } from 'utils/asyncModal';
import { filterCostAndGM } from 'utils/filterCostAndGM';
import { routes } from 'utils/routes';
import {
  sortableIdColumn,
  sortableNumberColumn,
  sortableStringColumn,
  sortableDollarWithCentsColumn,
  asKeyOf,
} from 'utils/tables';
import { isTenantErpTypeP21 } from 'utils/tenants';

import { OrderAssemblyLinesAgnosticModal } from '../modals/OrderAssemblyLinesAgnosticModal';

export const OrderLinesAgnosticTable = ({ orderLines }: { orderLines: SalesOrderDetailLineDTO[] }) => {
  const { activeTenant } = useGlobalApp();

  const lineItemLineTypeMap: Obj<string> = {
    'special-order': 'Special Order',
    'production-order': 'Production Order',
    'drop-ship': 'Drop Ship',
    backorder: 'Backorder',
    hold: 'Hold',
    transfer: 'Transfer',
    'future-production-order': 'Future Production Order',
    canceled: 'Canceled',
    quote: 'Quote',
    'non-stock': 'NonStock',
  };

  const orderLineColumns: (ColumnType<SalesOrderDetailLineDTO> | null)[] = [
    sortableIdColumn({
      title: 'Item ID',
      dataIndex: asKeyOf<SalesOrderDetailLineDTO>('itemCode'),
      fixed: 'left',
      render: (itemCode: string, record: SalesOrderDetailLineDTO) => (
        <>
          <Link to={routes.sales.itemDetails(itemCode)}> {itemCode} </Link>
          {record.lineItemType === 'assembly' && record.orderLineId && (
            <Button
              size="small"
              onClick={() => {
                if (record.itemCode) {
                  showAsyncModal(OrderAssemblyLinesAgnosticModal, {
                    itemId: record.itemCode,
                    orderLineUid: record.orderLineId || '0',
                    orderLines,
                  });
                }
              }}
            >
              <DeploymentUnitOutlined />
            </Button>
          )}
        </>
      ),
    }),
    sortableStringColumn({
      title: 'Item',
      fixed: 'left',
      dataIndex: asKeyOf<SalesOrderDetailLineDTO>('itemName'),
    }),
    sortableStringColumn({
      title: 'Status',
      dataIndex: asKeyOf<SalesOrderDetailLineDTO>('status'),
      render: (status: ErpOrderStatus) => <ErpOrderStatusBadge erpOrderStatus={status} />,
    }),
    isTenantErpTypeP21(activeTenant.erpType)
      ? {
          title: 'Disposition',
          dataIndex: asKeyOf<SalesOrderDetailLineDTO>('lineType'),
          render: (lineType: string) => lineItemLineTypeMap[lineType] || lineType || '-',
        }
      : null,
    sortableStringColumn({
      title: 'UOM',
      dataIndex: asKeyOf<SalesOrderDetailLineDTO>('unitOfMeasure'),
    }),
    sortableNumberColumn({
      title: 'Qty Ordered',
      dataIndex: asKeyOf<SalesOrderDetailLineDTO>('quantityOrdered'),
    }),
    isTenantErpTypeP21(activeTenant.erpType)
      ? sortableNumberColumn({
          title: 'Qty Allocated',
          dataIndex: asKeyOf<SalesOrderDetailLineDTO>('quantityAllocated'),
        })
      : null,
    isTenantErpTypeP21(activeTenant.erpType)
      ? sortableNumberColumn({
          title: 'Qty on Pick Ticket',
          dataIndex: asKeyOf<SalesOrderDetailLineDTO>('quantityOnPickTickets'),
        })
      : null,
    isTenantErpTypeP21(activeTenant.erpType)
      ? sortableNumberColumn({
          title: 'Qty Canceled',
          dataIndex: asKeyOf<SalesOrderDetailLineDTO>('quantityCanceled'),
        })
      : null,
    isTenantErpTypeP21(activeTenant.erpType)
      ? sortableNumberColumn({
          title: 'Qty Invoiced',
          dataIndex: asKeyOf<SalesOrderDetailLineDTO>('quantityInvoiced'),
        })
      : null,
    sortableNumberColumn({
      title: 'Qty Open',
      dataIndex: asKeyOf<SalesOrderDetailLineDTO>('quantityOpen'),
    }),
    sortableDollarWithCentsColumn({
      title: 'Unit Price',
      dataIndex: asKeyOf<SalesOrderDetailLineDTO>('unitPrice'),
    }),
    sortableDollarWithCentsColumn({
      title: 'Unit Cost',
      dataIndex: asKeyOf<SalesOrderDetailLineDTO>('unitCost'),
    }),
    sortableDollarWithCentsColumn({
      title: 'Ext. Price',
      dataIndex: asKeyOf<SalesOrderDetailLineDTO>('lineTotal'),
    }),
    sortableDollarWithCentsColumn({
      title: 'Ext. Margin',
      dataIndex: asKeyOf<SalesOrderDetailLineDTO>('lineGrossMargin'),
    }),
  ];

  return (
    <Table
      columns={orderLineColumns.filter(filterCostAndGM)}
      scroll={{ x: true }}
      data={orderLines.filter((line) => !line.parentOrderLineId || line.parentOrderLineId === '0')}
    />
  );
};
